import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContextSupabase';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, BarChart3, Search, ArrowRight, X, Calendar, DollarSign, FileText, ClipboardCheck, UserCheck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Dialog } from '@headlessui/react';
import type { Agency, UserProfile, Client, Influencer, Collab } from '../lib/supabase';

export default function SuperAdmin() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<'agencies' | 'users' | 'clients' | 'influencers' | 'collabs' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Collab | null>(null);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const [isAgencyModalOpen, setIsAgencyModalOpen] = useState(false);

  useEffect(() => {
    if (!currentUser?.email || currentUser.email !== 'hosni@swipe-upp.com') {
      navigate('/');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setIsLoading(true);
        
        const agenciesSnapshot = await getDocs(collection(db, 'agencies'));
        const agenciesData = agenciesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Agency[];
        setAgencies(agenciesData);

        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as UserProfile[];
        setUsers(usersData);

        const clientsSnapshot = await getDocs(collection(db, 'clients'));
        const clientsData = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Client[];
        setClients(clientsData);

        const influencersSnapshot = await getDocs(collection(db, 'influencers'));
        const influencersData = influencersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Influencer[];
        setInfluencers(influencersData);

        const collabsSnapshot = await getDocs(collection(db, 'collabs'));
        const collabsData = collabsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Collab[];
        setCollabs(collabsData);

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  const getAgencyStats = (agencyId: string) => {
    const agencyUsers = users.filter(user => user.agencyId === agencyId);
    const agencyClients = clients.filter(client => client.agencyId === agencyId);
    const agencyInfluencers = influencers.filter(inf => inf.agencyId === agencyId);
    const agencyCollabs = collabs.filter(collab => collab.agencyId === agencyId);

    return {
      users: agencyUsers.length,
      clients: agencyClients.length,
      influencers: agencyInfluencers.length,
      collabs: agencyCollabs.length,
      revenue: agencyCollabs.reduce((total, collab) => {
        const collabTotal = collab.influencers?.reduce((sum, inf) => 
          sum + parseFloat(inf.rate || '0'), 0) || 0;
        return total + collabTotal;
      }, 0)
    };
  };

  const formatCurrency = (amount: number | string) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleSectionClick = (section: 'agencies' | 'users' | 'clients' | 'influencers' | 'collabs') => {
    setSelectedSection(section);
    setIsDetailsModalOpen(true);
  };

  const handleCampaignClick = (campaign: Collab, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCampaign(campaign);
    setIsCampaignModalOpen(true);
  };

  const handleAgencyClick = (agency: Agency, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgency(agency);
    setIsAgencyModalOpen(true);
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.invoice_sent || campaign.payment_received) return { label: 'Terminée', className: 'bg-green-500/20 text-green-300 border-green-500/20' };
    if (campaign.quote_sent || campaign.contract_signed) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' };
    return { label: 'En attente', className: 'bg-blue-500/20 text-blue-300 border-blue-500/20' };
  };

  const filteredAgencies = agencies.filter(agency =>
    agency.name.toLowerCase().includes(search.toLowerCase())
  );

  const totalStats = {
    agencies: agencies.length,
    users: users.length,
    clients: clients.length,
    influencers: influencers.length,
    collabs: collabs.length,
    revenue: collabs.reduce((total, collab) => {
      const collabTotal = collab.influencers?.reduce((sum, inf) => 
        sum + parseFloat(inf.rate || '0'), 0) || 0;
      return total + collabTotal;
    }, 0)
  };

  const getAgencyDetails = (agencyId: string) => {
    const agencyUsers = users.filter(user => user.agencyId === agencyId);
    const agencyClients = clients.filter(client => client.agencyId === agencyId);
    const agencyInfluencers = influencers.filter(inf => inf.agencyId === agencyId);
    const agencyCollabs = collabs.filter(collab => collab.agencyId === agencyId);

    return {
      users: agencyUsers,
      clients: agencyClients,
      influencers: agencyInfluencers,
      collabs: agencyCollabs,
      stats: getAgencyStats(agencyId)
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">Super Admin Dashboard</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/superadmin/user-approval')}
              className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
            >
              <UserCheck className="w-5 h-5" />
              Approbation des utilisateurs
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une agence..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-dark-50 border border-dark-200 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div 
            onClick={() => handleSectionClick('agencies')}
            className="bg-dark-50 rounded-xl p-6 border border-dark-200 cursor-pointer hover:bg-dark-100/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Agences</h3>
              <Building2 className="w-5 h-5 text-accent-blue" />
            </div>
            <p className="text-2xl font-bold text-white">{totalStats.agencies}</p>
          </div>
          <div 
            onClick={() => handleSectionClick('users')}
            className="bg-dark-50 rounded-xl p-6 border border-dark-200 cursor-pointer hover:bg-dark-100/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Utilisateurs</h3>
              <Users className="w-5 h-5 text-accent-purple" />
            </div>
            <p className="text-2xl font-bold text-white">{totalStats.users}</p>
          </div>
          <div 
            onClick={() => handleSectionClick('clients')}
            className="bg-dark-50 rounded-xl p-6 border border-dark-200 cursor-pointer hover:bg-dark-100/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Clients</h3>
              <Building2 className="w-5 h-5 text-accent-blue" />
            </div>
            <p className="text-2xl font-bold text-white">{totalStats.clients}</p>
          </div>
          <div 
            onClick={() => handleSectionClick('influencers')}
            className="bg-dark-50 rounded-xl p-6 border border-dark-200 cursor-pointer hover:bg-dark-100/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Influenceurs</h3>
              <Users className="w-5 h-5 text-accent-purple" />
            </div>
            <p className="text-2xl font-bold text-white">{totalStats.influencers}</p>
          </div>
          <div 
            onClick={() => handleSectionClick('collabs')}
            className="bg-dark-50 rounded-xl p-6 border border-dark-200 cursor-pointer hover:bg-dark-100/50 transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">Campagnes</h3>
              <BarChart3 className="w-5 h-5 text-accent-blue" />
            </div>
            <p className="text-2xl font-bold text-white">{totalStats.collabs}</p>
          </div>
          <div className="bg-dark-50 rounded-xl p-6 border border-dark-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-white">CA Total</h3>
              <BarChart3 className="w-5 h-5 text-accent-purple" />
            </div>
            <p className="text-2xl font-bold text-white">{formatCurrency(totalStats.revenue)}</p>
          </div>
        </div>

        <div className="bg-dark-50 rounded-xl border border-dark-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Agence</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Date de création</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Utilisateurs</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Clients</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Influenceurs</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">Campagnes</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-400">CA Total</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-400">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-200">
                {filteredAgencies.map((agency) => {
                  const stats = getAgencyStats(agency.id!);
                  return (
                    <tr 
                      key={agency.id} 
                      className="hover:bg-dark-100/50 cursor-pointer"
                      onClick={(e) => handleAgencyClick(agency, e)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <div className="font-medium text-white">{agency.name}</div>
                            <div className="text-sm text-gray-400">ID: {agency.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-400">{formatDate(agency.createdAt)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-accent-blue" />
                          <span className="text-white">{stats.users}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-accent-purple" />
                          <span className="text-white">{stats.clients}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-accent-blue" />
                          <span className="text-white">{stats.influencers}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-accent-purple" />
                          <span className="text-white">{stats.collabs}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-white">{formatCurrency(stats.revenue)}</td>
                      <td className="py-3 px-4 text-right">
                        <ArrowRight className="w-5 h-5 text-accent-blue inline-block" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Agency Details Modal */}
      <Dialog
        open={isAgencyModalOpen}
        onClose={() => setIsAgencyModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-6xl w-full mx-4 p-6 border border-dark-200">
            {selectedAgency && (
              <>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={() => setIsAgencyModalOpen(false)}
                    className="text-gray-400 hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{selectedAgency.name}</h2>
                      <p className="text-gray-400">Créée le {formatDate(selectedAgency.createdAt)}</p>
                    </div>
                  </div>

                  {selectedAgency.id && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-6">
                        <div className="bg-dark-100 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-white mb-4">Utilisateurs</h3>
                          <div className="space-y-3">
                            {getAgencyDetails(selectedAgency.id).users.map((user) => (
                              <div key={user.id} className="flex items-center justify-between">
                                <div>
                                  <p className="text-white">{user.firstName} {user.lastName}</p>
                                  <p className="text-sm text-gray-400">{user.email}</p>
                                </div>
                                <span className="text-sm text-gray-400">{user.role}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-dark-100 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-white mb-4">Clients</h3>
                          <div className="space-y-3">
                            {getAgencyDetails(selectedAgency.id).clients.map((client) => (
                              <div key={client.id} className="flex items-center justify-between">
                                <div>
                                  <p className="text-white">{client.brand_name}</p>
                                  <p className="text-sm text-gray-400">
                                    {client.contacts[0]?.name} • {client.contacts[0]?.email}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <div className="bg-dark-100 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-white mb-4">Campagnes</h3>
                          <div className="space-y-3">
                            {getAgencyDetails(selectedAgency.id).collabs.map((collab) => (
                              <div
                                key={collab.id}
                                className="bg-dark-200 rounded-lg p-3 cursor-pointer hover:bg-dark-300/50 transition-colors"
                                onClick={(e) => handleCampaignClick(collab, e)}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-white">{collab.name}</p>
                                    <p className="text-sm text-gray-400">{collab.brand}</p>
                                  </div>
                                  <div className="text-right">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCampaignStatus(collab).className}`}>
                                      {getCampaignStatus(collab).label}
                                    </span>
                                    <div className="mt-1 text-sm font-medium text-accent-blue">
                                      {formatCurrency(collab.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="bg-dark-100 rounded-lg p-4">
                          <h3 className="text-lg font-medium text-white mb-4">Influenceurs</h3>
                          <div className="space-y-3">
                            {getAgencyDetails(selectedAgency.id).influencers.map((influencer) => (
                              <div key={influencer.id} className="flex items-center justify-between">
                                <div>
                                  <p className="text-white">{influencer.fullName}</p>
                                  <div className="flex items-center gap-2">
                                    {influencer.platforms.map((platform, index) => (
                                      <span key={index} className="text-sm text-gray-400">
                                        {platform.name} • {platform.audience}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </Dialog>

      {/* Details Modal */}
      <Dialog
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsDetailsModalOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {selectedSection === 'collabs' && (
                <>
                  <h2 className="text-2xl font-bold text-white">Toutes les campagnes</h2>
                  <div className="space-y-4">
                    {collabs.map((collab) => (
                      <div 
                        key={collab.id} 
                        className="bg-dark-100 rounded-lg p-4 cursor-pointer hover:bg-dark-200/50 transition-colors"
                        onClick={(e) => handleCampaignClick(collab, e)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="text-white font-medium">{collab.name}</h3>
                            <p className="text-sm text-gray-400">{collab.brand}</p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCampaignStatus(collab).className}`}>
                            {getCampaignStatus(collab).label}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <Users className="w-4 h-4" />
                              <span>{collab.manager}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              <BarChart3 className="w-4 h-4" />
                              <span>Progression: {collab.post_progress}%</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-400">Budget total</p>
                            <p className="text-lg font-medium text-accent-blue">
                              {formatCurrency(collab.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0))}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {selectedSection === 'users' && (
                <>
                  <h2 className="text-2xl font-bold text-white">Tous les utilisateurs</h2>
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div key={user.id} className="bg-dark-100 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-medium">{user.firstName} {user.lastName}</h3>
                            <p className="text-sm text-gray-400">{user.email}</p>
                          </div>
                          <span className="text-sm text-gray-400">{user.role}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {selectedSection === 'clients' && (
                <>
                  <h2 className="text-2xl font-bold text-white">Tous les clients</h2>
                  <div className="space-y-4">
                    {clients.map((client) => {
                      const clientCampaigns = collabs.filter(collab => collab.brand === client.brand_name);
                      return (
                        <div key={client.id} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-white font-medium">{client.brand_name}</h3>
                          </div>
                          <div className="space-y-2">
                            {client.contacts.map((contact, index) => (
                              <div key={index} className="text-sm text-gray-400">
                                {contact.name} • {contact.email}
                              </div>
                            ))}
                          </div>
                          {clientCampaigns.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <h4 className="text-sm font-medium text-gray-200">Campagnes</h4>
                              {clientCampaigns.map((campaign) => (
                                <div
                                  key={campaign.id}
                                  className="bg-dark-200 rounded-lg p-3 cursor-pointer hover:bg-dark-300/50 transition-colors"
                                  onClick={(e) => handleCampaignClick(campaign, e)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-white">{campaign.name}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <Calendar className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm text-gray-400">{formatDate(campaign.date)}</span>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCampaignStatus(campaign).className}`}>
                                        {getCampaignStatus(campaign).label}
                                      </span>
                                      <div className="mt-1 text-sm font-medium text-accent-blue">
                                        {formatCurrency(campaign.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {selectedSection === 'influencers' && (
                <>
                  <h2 className="text-2xl font-bold text-white">Tous les influenceurs</h2>
                  <div className="space-y-4">
                    {influencers.map((influencer) => {
                      const influencerCampaigns = collabs.filter(collab => 
                        collab.influencers.some(inf => inf.fullName === influencer.fullName)
                      );
                      return (
                
                        <div key={influencer.id} className="bg-dark-100 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="text-white font-medium">{influencer.fullName}</h3>
                              <div className="flex items-center gap-2 mt-1">
                                {influencer.platforms.map((platform, index) => (
                                  <span key={index} className="text-sm text-gray-400">
                                    {platform.name} • {platform.audience}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          {influencerCampaigns.length > 0 && (
                            <div className="mt-4 space-y-3">
                              <h4 className="text-sm font-medium text-gray-200">Campagnes</h4>
                              {influencerCampaigns.map((campaign) => (
                                <div
                                  key={campaign.id}
                                  className="bg-dark-200 rounded-lg p-3 cursor-pointer hover:bg-dark-300/50 transition-colors"
                                  onClick={(e) => handleCampaignClick(campaign, e)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="text-white">{campaign.name}</p>
                                      <p className="text-sm text-gray-400">{campaign.brand}</p>
                                    </div>
                                    <div className="text-right">
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCampaignStatus(campaign).className}`}>
                                        {getCampaignStatus(campaign).label}
                                      </span>
                                      <div className="mt-1 text-sm font-medium text-accent-blue">
                                        {formatCurrency(
                                          campaign.influencers.find(inf => inf.fullName === influencer.fullName)?.rate || '0'
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Campaign Details Modal */}
      <Dialog
        open={isCampaignModalOpen}
        onClose={() => setIsCampaignModalOpen(false)}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-dark-50 rounded-xl max-w-4xl w-full mx-4 p-6 border border-dark-200">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsCampaignModalOpen(false)}
                className="text-gray-400 hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {selectedCampaign && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedCampaign.name}</h2>
                    <p className="text-gray-400">{selectedCampaign.brand}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getCampaignStatus(selectedCampaign).className}`}>
                    {getCampaignStatus(selectedCampaign).label}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-dark-100 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-white mb-3">Informations</h3>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-gray-400">
                          <Calendar className="w-4 h-4" />
                          <span>Date: {formatDate(selectedCampaign.date)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <Users className="w-4 h-4" />
                          <span>Chef de projet: {selectedCampaign.manager}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <BarChart3 className="w-4 h-4" />
                          <span>Progression: {selectedCampaign.post_progress}%</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-400">
                          <DollarSign className="w-4 h-4" />
                          <span>Budget total: {formatCurrency(
                            selectedCampaign.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0)
                          )}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-dark-100 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-white mb-3">Influenceurs</h3>
                      <div className="space-y-3">
                        {selectedCampaign.influencers.map((influencer, index) => (
                          <div key={index} className="bg-dark-200 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white">{influencer.fullName}</span>
                              <span className="text-accent-blue font-medium">
                                {formatCurrency(influencer.rate || '0')}
                              </span>
                            </div>
                            {influencer.deliverables?.map((deliverable, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm text-gray-400">
                                <span>{deliverable.type}</span>
                                <span>{formatCurrency(deliverable.price)}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}